import { useState, useRef, useMemo } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { Button } from "./ui/button";
import { Loader2, Save, X, Eye, Edit2 } from "lucide-react";
import { toast } from "sonner";

const API = "/api";

// Sanitize HTML content to fix word wrapping issues
const sanitizeContent = (html) => {
  if (!html) return html;
  
  // Replace &nbsp; with regular spaces (but preserve single &nbsp; between tags)
  // This regex replaces multiple consecutive &nbsp; or &nbsp; between words
  let sanitized = html
    // Replace &nbsp; surrounded by word characters with regular space
    .replace(/(\w)&nbsp;(\w)/g, '$1 $2')
    // Replace multiple consecutive &nbsp; with single space
    .replace(/(&nbsp;){2,}/g, ' ')
    // Replace &nbsp; at start/end of text nodes
    .replace(/>(&nbsp;)+/g, '> ')
    .replace(/(&nbsp;)+</g, ' <')
    // Replace remaining &nbsp; between words with regular space
    .replace(/&nbsp;/g, ' ');
  
  return sanitized;
};

export default function PageEditor({ 
  pageSlug, 
  initialContent, 
  onSave, 
  onCancel,
  title 
}) {
  const [content, setContent] = useState(initialContent || "");
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const quillRef = useRef(null);

  // Quill toolbar configuration with image and video support
  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'indent': '-1' }, { 'indent': '+1' }],
        [{ 'align': [] }],
        ['link', 'image', 'video'],
        ['blockquote', 'code-block'],
        ['clean']
      ],
    },
    clipboard: {
      matchVisual: false,
    },
  }), []);

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'list', 'bullet', 'indent',
    'align',
    'link', 'image', 'video',
    'blockquote', 'code-block'
  ];

  // Get sanitized content for preview and save
  const getSanitizedContent = () => sanitizeContent(content);

  const handleSave = async () => {
    setSaving(true);
    try {
      const sanitizedContent = getSanitizedContent();
      
      const response = await fetch(`${API}/pages/${pageSlug}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ content: sanitizedContent })
      });

      if (response.ok) {
        toast.success("Page saved successfully!");
        if (onSave) onSave(sanitizedContent);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to save page");
      }
    } catch (err) {
      toast.error("Error saving page");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="page-editor">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-xl font-semibold text-foreground">
          Editing: {title}
        </h2>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPreviewMode(!previewMode)}
            className="gap-1.5"
            data-testid="preview-toggle"
          >
            {previewMode ? (
              <>
                <Edit2 className="w-4 h-4" />
                Edit
              </>
            ) : (
              <>
                <Eye className="w-4 h-4" />
                Preview
              </>
            )}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onCancel}
            className="gap-1.5"
            data-testid="cancel-edit"
          >
            <X className="w-4 h-4" />
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={saving}
            className="gap-1.5"
            data-testid="save-page"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Editor / Preview */}
      {previewMode ? (
        <div 
          className="page-content bg-muted/30 rounded-lg p-6 min-h-[400px] prose prose-lg max-w-none
            prose-headings:text-foreground
            prose-p:text-foreground/80
            prose-li:text-foreground/80
            prose-strong:text-foreground
            prose-a:text-primary hover:prose-a:text-primary/80
            prose-ul:list-disc prose-ol:list-decimal
            prose-img:rounded-lg prose-img:shadow-lg"
          style={{ overflowWrap: 'break-word' }}
          dangerouslySetInnerHTML={{ __html: getSanitizedContent() }}
          data-testid="preview-content"
        />
      ) : (
        <div className="rounded-lg overflow-hidden border border-border">
          <ReactQuill
            ref={quillRef}
            theme="snow"
            value={content}
            onChange={setContent}
            modules={modules}
            formats={formats}
            placeholder="Start writing your page content here..."
            className="page-editor-quill"
            data-testid="quill-editor"
          />
        </div>
      )}

      {/* Help text */}
      <div className="text-sm text-muted-foreground space-y-1">
        <p><strong className="text-foreground">Tips:</strong></p>
        <ul className="list-disc list-inside space-y-1 ml-2">
          <li>Use the image button to insert images (paste a URL or upload)</li>
          <li>Use the video button to embed YouTube videos (paste the YouTube URL)</li>
          <li>Headers, lists, and formatting will be preserved</li>
        </ul>
      </div>

      {/* Custom styles for Quill editor - always light background for editing */}
      <style>{`
        .page-editor-quill .ql-container {
          min-height: 350px;
          font-size: 16px;
          background: white;
        }
        .page-editor-quill .ql-editor {
          min-height: 350px;
          color: #1e293b;
        }
        .page-editor-quill .ql-toolbar {
          background: #f8fafc;
          border-color: #e2e8f0;
        }
        .page-editor-quill .ql-container {
          border-color: #e2e8f0;
        }
        .page-editor-quill .ql-snow .ql-picker {
          color: #334155;
        }
        .page-editor-quill .ql-snow .ql-stroke {
          stroke: #334155;
        }
        .page-editor-quill .ql-snow .ql-fill {
          fill: #334155;
        }
        .page-editor-quill .ql-editor.ql-blank::before {
          color: #94a3b8;
          font-style: normal;
        }
        /* Video embed styles */
        .page-editor-quill .ql-video {
          width: 100%;
          max-width: 640px;
          height: 360px;
        }
        /* Ensure links are visible in editor */
        .page-editor-quill .ql-editor a {
          color: #0ea5e9;
        }
      `}</style>
    </div>
  );
}
